// The dashboard's chart palette and the few shared specs every chart obeys.
//
// The values below are a validated set, not a taste call: the categorical slots are
// assigned in fixed order and never cycled, the ordinal ramp is one hue stepped
// light -> dark, and status colours are reserved for state and never used as a series.
// Every set was checked against the card surface (#ffffff) for lightness band, chroma,
// colour-vision separation and contrast. Three of the categorical slots sit under 3:1
// against white, which is why every chart that uses them also carries visible labels or
// a legend — colour never carries meaning on its own here.
//
// The app's brand green is chrome, deliberately not a series colour: a hue that also
// paints buttons and headers reads as "the app" rather than as "this measure".

export const CHART_INK = {
  surface: "#ffffff",
  gridline: "#e1e0d9",
  axis: "#c3c2b7",
  primary: "#0b0b0b",
  secondary: "#52514e",
  muted: "#898781",
} as const;

/** Categorical slots, in fixed order. A series keeps its slot when others are filtered out. */
export const CHART_SERIES = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4"] as const;

/** One hue, light -> dark: for ordered categories (age bands, progress states). */
export const CHART_ORDINAL_4 = ["#86b6ef", "#5598e7", "#2a78d6", "#184f95"] as const;
export const CHART_ORDINAL_3 = ["#86b6ef", "#2a78d6", "#184f95"] as const;

/** The lighter step of the ordinal ramp that a meter's unfilled track is drawn in. */
export const CHART_TRACK = "#cde2fb";

/** Reserved for state. Never a series colour, and never shown without a label. */
export const CHART_STATUS = {
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",
} as const;

/** Marks stay thin and the grid stays recessive — the data is the only loud thing. */
export const CHART_MARKS = {
  barSize: 18,
  /** Rounded data-end, square at the baseline (horizontal bars grow to the right). */
  barRadius: [0, 4, 4, 0] as [number, number, number, number],
  lineWidth: 2,
  areaOpacity: 0.1,
  /** The surface gap that separates touching marks, instead of a stroke around them. */
  gap: 2,
} as const;

export const CHART_AXIS_TICK = { fill: CHART_INK.muted, fontSize: 11 } as const;

/** Compact money for an axis tick or a chart label, where the full figure won't fit. */
export function fmtMVRCompact(laari: number | null | undefined): string {
  if (laari == null) return "-";
  const mvr = laari / 100;
  const abs = Math.abs(mvr);
  if (abs >= 1_000_000) return `${(mvr / 1_000_000).toLocaleString("en-US", { maximumFractionDigits: 1 })}M`;
  if (abs >= 1_000) return `${(mvr / 1_000).toLocaleString("en-US", { maximumFractionDigits: 1 })}K`;
  return mvr.toLocaleString("en-US", { maximumFractionDigits: 0 });
}
